import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminArtworks, reset, deleteArtwork, updateArtworkFields, fetchArtworkCategories, saveArtworkCategory, restoreArtwork } from '../../features/artwork/artworkSlice';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';

function AdminArtworks() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [categoryDrafts, setCategoryDrafts] = useState({});
    const [savingCategory, setSavingCategory] = useState(null);
    const isTrashView = location.pathname.endsWith('/trash');
    
    const { user } = useSelector((state) => state.auth);
    const {
        artworks,
        isLoading,
        isError,
        message,
        categories,
        categoriesLoading,
        categoriesError,
        categoriesMessage,
    } = useSelector((state) => state.artwork);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        dispatch(getAdminArtworks());
        dispatch(fetchArtworkCategories());

        return () => {
            dispatch(reset());
        };
    }, [user, navigate, dispatch]);

    useEffect(() => {
        if (isError) {
            toast.error(message);
        }
    }, [isError, message]);

    useEffect(() => {
        if (categoriesError) {
            toast.error(categoriesMessage || 'Unable to load categories');
        }
    }, [categoriesError, categoriesMessage]);

    const handleDeleteArtwork = (id) => {
        if (window.confirm('Are you sure you want to delete this artwork?')) {
            dispatch(deleteArtwork(id));
        }
    };

    const handleRestoreArtwork = (id) => {
        dispatch(restoreArtwork(id));
    };

    const handleTogglePrints = (artwork) => {
        const updatedValue = !artwork.used_for_prints;
        dispatch(updateArtworkFields({ id: artwork._id, data: { used_for_prints: updatedValue } }))
            .unwrap()
            .then(() => {
                toast.success(`Prints ${updatedValue ? 'enabled' : 'disabled'} for "${artwork.title}"`);
            })
            .catch((err) => {
                toast.error(err || 'Failed to update prints flag');
            });
    };

    const artworksByStatus = useMemo(() => {
        if (!artworks) return [];
        return artworks.filter((artwork) => isTrashView ? artwork.status === 'inactive' : artwork.status === 'active');
    }, [artworks, isTrashView]);

    const artworkCategories = useMemo(() => {
        if (!artworksByStatus) return [];
        const unique = new Set();
        artworksByStatus.forEach((artwork) => {
            if (artwork.category) {
                unique.add(artwork.category);
            }
        });
        return Array.from(unique).sort();
    }, [artworksByStatus]);

    const filteredArtworks = useMemo(() => {
        if (!artworksByStatus) return [];
        if (categoryFilter === 'all') return artworksByStatus;
        return artworksByStatus.filter((artwork) => artwork.category === categoryFilter);
    }, [artworksByStatus, categoryFilter]);

    const categoryList = useMemo(() => Array.isArray(categories) ? categories : [], [categories]);

    const updateCategoryDraft = (categoryName, updates) => {
        setCategoryDrafts((prev) => ({
            ...prev,
            [categoryName]: {
                ...prev[categoryName],
                ...updates,
            },
        }));
    };

    const clearCategoryDraft = (categoryName) => {
        setCategoryDrafts((prev) => {
            if (!(categoryName in prev)) {
                return prev;
            }
            const { [categoryName]: removed, ...rest } = prev;
            return rest;
        });
    };

    const hasCategoryChanges = (category) => {
        const draft = categoryDrafts[category.category];
        if (!draft) return false;

        if (draft.display_name !== undefined && draft.display_name !== category.display_name) {
            return true;
        }

        if (draft.show_on_home !== undefined && draft.show_on_home !== category.show_on_home) {
            return true;
        }

        if (draft.sort_order !== undefined) {
            if (draft.sort_order === null) {
                return true;
            }
            if (draft.sort_order !== category.sort_order) {
                return true;
            }
        }

        if (draft.print_product_id !== undefined && draft.print_product_id !== category.print_product_id) {
            return true;
        }

        if (draft.print_display_name !== undefined && draft.print_display_name !== category.print_display_name) {
            return true;
        }

        if (draft.print_enabled !== undefined && draft.print_enabled !== category.print_enabled) {
            return true;
        }

        if (draft.print_sort_order !== undefined) {
            if (draft.print_sort_order === null) {
                return true;
            }
            if (draft.print_sort_order !== category.print_sort_order) {
                return true;
            }
        }

        return false;
    };

    const isCategoryDraftInvalid = (category) => {
        const draft = categoryDrafts[category.category];
        if (!draft) return false;
        const hasInvalidSort = draft.sort_order === null;
        const hasInvalidPrintSort = draft.print_sort_order === null;
        const missingPrintProduct =
            (draft.print_enabled ?? category.print_enabled) &&
            !(draft.print_product_id ?? category.print_product_id);
        return hasInvalidSort || hasInvalidPrintSort || missingPrintProduct;
    };

    const handleDisplayNameChange = (categoryName, value) => {
        updateCategoryDraft(categoryName, { display_name: value });
    };

    const handleSortOrderChange = (categoryName, value) => {
        if (value === '') {
            updateCategoryDraft(categoryName, { sort_order: null });
            return;
        }
        const parsed = Number(value);
        updateCategoryDraft(categoryName, {
            sort_order: Number.isFinite(parsed) ? parsed : null,
        });
    };

    const handleShowOnHomeChange = (categoryName, checked) => {
        updateCategoryDraft(categoryName, { show_on_home: checked });
    };

    const handlePrintProductChange = (categoryName, value) => {
        updateCategoryDraft(categoryName, { print_product_id: value });
    };

    const handlePrintDisplayNameChange = (categoryName, value) => {
        updateCategoryDraft(categoryName, { print_display_name: value });
    };

    const handlePrintEnabledChange = (categoryName, checked) => {
        updateCategoryDraft(categoryName, { print_enabled: checked });
    };

    const handlePrintSortOrderChange = (categoryName, value) => {
        if (value === '') {
            updateCategoryDraft(categoryName, { print_sort_order: null });
            return;
        }
        const parsed = Number(value);
        updateCategoryDraft(categoryName, {
            print_sort_order: Number.isFinite(parsed) ? parsed : null,
        });
    };

    const handleSaveCategory = (category) => {
        const draft = categoryDrafts[category.category] || {};
        const resolvedDisplayName = (draft.display_name ?? category.display_name ?? category.category).trim();
        const resolvedShowOnHome = typeof draft.show_on_home === 'boolean' ? draft.show_on_home : category.show_on_home;
        const sortOrderCandidate = draft.sort_order !== undefined ? draft.sort_order : category.sort_order;
        const resolvedSortOrder =
            typeof sortOrderCandidate === 'number' && Number.isFinite(sortOrderCandidate)
                ? sortOrderCandidate
                : 999;

        const resolvedPrintProductId = (draft.print_product_id ?? category.print_product_id ?? '').trim();
        const resolvedPrintEnabled = typeof draft.print_enabled === 'boolean'
            ? draft.print_enabled
            : (typeof category.print_enabled === 'boolean' ? category.print_enabled : Boolean(resolvedPrintProductId));
        const resolvedPrintDisplayName = (draft.print_display_name ?? category.print_display_name ?? resolvedDisplayName).trim();
        const printSortCandidate = draft.print_sort_order !== undefined
            ? draft.print_sort_order
            : category.print_sort_order;
        const resolvedPrintSortOrder =
            typeof printSortCandidate === 'number' && Number.isFinite(printSortCandidate)
                ? printSortCandidate
                : 999;

        if (resolvedPrintEnabled && !resolvedPrintProductId) {
            toast.error('Provide a print product ID or disable prints for this category.');
            return;
        }

        setSavingCategory(category.category);
        dispatch(
            saveArtworkCategory({
                category: category.category,
                data: {
                    display_name: resolvedDisplayName,
                    show_on_home: resolvedShowOnHome,
                    sort_order: resolvedSortOrder,
                    print_product_id: resolvedPrintProductId,
                    print_display_name: resolvedPrintDisplayName,
                    print_sort_order: resolvedPrintSortOrder,
                    print_enabled: resolvedPrintEnabled,
                },
            })
        )
            .unwrap()
            .then(() => {
                toast.success(`Updated category "${category.category}"`);
                clearCategoryDraft(category.category);
            })
            .catch((error) => {
                toast.error(error || 'Failed to update category');
            })
            .finally(() => {
                setSavingCategory(null);
            });
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div className="admin-container">
            <h1 className="admin-header">{isTrashView ? 'Artwork Trash' : 'Artwork List'}</h1>
            {!isTrashView && (
            <div className="form-section" style={{ marginBottom: '24px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                    }}
                >
                    <h2 style={{ margin: 0 }}>Artwork Categories</h2>
                    {categoriesLoading && (
                        <span style={{ fontSize: '0.9em', color: 'var(--color-ink-muted, #6b7280)' }}>Loading categories…</span>
                    )}
                </div>
                {!categoriesLoading && categoriesError && (
                    <p style={{ marginTop: '8px', color: 'var(--color-accent, #e24a2c)' }}>
                        {categoriesMessage || 'Unable to load categories. The artworks themselves are still available.'}
                    </p>
                )}
                {(!categoriesLoading && categoryList.length === 0) ? (
                    <p style={{ marginTop: '12px', color: 'var(--color-ink-muted, #6b7280)' }}>
                        No categories available yet. Add artworks to create categories automatically.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto', marginTop: '16px' }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Display Name</th>
                                    <th style={{ width: '120px' }}>Home Order</th>
                                    <th style={{ width: '140px' }}>Show on Home</th>
                                    <th style={{ minWidth: '180px' }}>Print Product ID</th>
                                    <th style={{ minWidth: '180px' }}>Print Title</th>
                                    <th style={{ width: '120px' }}>Print Order</th>
                                    <th style={{ width: '120px' }}>Prints Enabled</th>
                                    <th style={{ width: '140px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categoryList.map((category) => {
                                    const draft = categoryDrafts[category.category] || {};
                                    const displayNameValue = draft.display_name ?? category.display_name ?? category.category;
                                    const sortOrderValue =
                                        draft.sort_order === null
                                            ? ''
                                            : (draft.sort_order ?? category.sort_order ?? '');
                                    const showOnHomeValue =
                                        typeof draft.show_on_home === 'boolean'
                                            ? draft.show_on_home
                                            : category.show_on_home;
                                    const printProductIdValue =
                                        draft.print_product_id ?? category.print_product_id ?? '';
                                    const printDisplayNameValue =
                                        draft.print_display_name ?? category.print_display_name ?? displayNameValue;
                                    const printSortOrderValue =
                                        draft.print_sort_order === null
                                            ? ''
                                            : (draft.print_sort_order ?? category.print_sort_order ?? '');
                                    const printEnabledValue =
                                        typeof draft.print_enabled === 'boolean'
                                            ? draft.print_enabled
                                            : (typeof category.print_enabled === 'boolean'
                                                ? category.print_enabled
                                                : Boolean(category.print_product_id));
                                    const pendingChanges = hasCategoryChanges(category);
                                    const draftInvalid = isCategoryDraftInvalid(category);
                                    const isSavingThisCategory = savingCategory === category.category;
                                    const canSave = pendingChanges && !draftInvalid && !isSavingThisCategory;
                                    return (
                                        <tr key={category.category}>
                                            <td style={{ fontWeight: 600 }}>{category.category}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={displayNameValue}
                                                    onChange={(event) =>
                                                        handleDisplayNameChange(category.category, event.target.value)
                                                    }
                                                    className="form-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={sortOrderValue}
                                                    onChange={(event) =>
                                                        handleSortOrderChange(category.category, event.target.value)
                                                    }
                                                    className="form-input"
                                                />
                                            </td>
                                            <td>
                                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(showOnHomeValue)}
                                                        onChange={(event) =>
                                                            handleShowOnHomeChange(category.category, event.target.checked)
                                                        }
                                                    />
                                                    <span>Visible</span>
                                                </label>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={printProductIdValue}
                                                    onChange={(event) =>
                                                        handlePrintProductChange(category.category, event.target.value)
                                                    }
                                                    placeholder="Product ID for this category"
                                                    className="form-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={printDisplayNameValue}
                                                    onChange={(event) =>
                                                        handlePrintDisplayNameChange(category.category, event.target.value)
                                                    }
                                                    className="form-input"
                                                    placeholder="Print product title"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={printSortOrderValue}
                                                    onChange={(event) =>
                                                        handlePrintSortOrderChange(category.category, event.target.value)
                                                    }
                                                    className="form-input"
                                                />
                                            </td>
                                            <td>
                                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(printEnabledValue)}
                                                        onChange={(event) =>
                                                            handlePrintEnabledChange(category.category, event.target.checked)
                                                        }
                                                    />
                                                    <span>Enabled</span>
                                                </label>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-primary"
                                                    type="button"
                                                    onClick={() => handleSaveCategory(category)}
                                                    disabled={!canSave}
                                                >
                                                    {isSavingThisCategory ? 'Saving…' : 'Save'}
                                                </button>
                                                {draftInvalid && (
                                                    <div style={{ fontSize: '0.75em', color: 'var(--color-accent)', marginTop: '4px' }}>
                                                        Enter a valid order or print product id
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    gap: '12px',
                    flexWrap: 'wrap'
                }}
            >
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {!isTrashView && (
                        <button className="btn btn-primary simple my-2" onClick={() => navigate('/admin/add-artwork')}>
                            Add New Artwork
                        </button>
                    )}
                    <button
                        className="btn btn-secondary simple my-2"
                        onClick={() => navigate(isTrashView ? '/admin/artworks' : '/admin/artworks/trash')}
                    >
                        {isTrashView ? 'Back to Artworks' : 'View Trash'}
                    </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label htmlFor="categoryFilter" style={{ fontWeight: 600 }}>Filter:</label>
                    <select
                        id="categoryFilter"
                        className="form-select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        style={{ minWidth: '200px' }}
                    >
                        <option value="all">All categories</option>
                        {artworkCategories.map((category) => (
                            <option key={category} value={category}>
                                {category}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Prints</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredArtworks && filteredArtworks.length > 0 ? (
                        filteredArtworks.map((artwork) => (
                            <tr key={artwork._id}>
                                <td onClick={() => navigate('/admin/add-artwork/' + artwork._id)} style={{ cursor: 'pointer' }}>
                                    {artwork.image && (
                                        <img 
                                            src={artwork.image.preview?.src || artwork.image.src} 
                                            alt={artwork.title} 
                                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} 
                                            loading="lazy"
                                        />
                                    )}
                                </td>
                                <td onClick={() => navigate('/admin/add-artwork/' + artwork._id)} style={{ cursor: 'pointer' }}>
                                    {artwork.title}
                                </td>
                                <td onClick={() => navigate('/admin/add-artwork/' + artwork._id)} style={{ cursor: 'pointer' }}>
                                    {artwork.category}
                                </td>
                                <td>
                                    {isTrashView ? (
                                        <span style={{ color: 'var(--color-ink-muted, #6b7280)' }}>Unavailable</span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTogglePrints(artwork);
                                            }}
                                            className="btn"
                                            style={{
                                                backgroundColor: artwork.used_for_prints ? 'var(--color-forest)' : 'var(--color-border)',
                                                color: artwork.used_for_prints ? 'var(--color-surface)' : 'var(--color-ink)',
                                                fontSize: '0.85em',
                                                padding: '6px 12px'
                                            }}
                                        >
                                            {artwork.used_for_prints ? 'Used for prints' : 'Not used'}
                                        </button>
                                    )}
                                </td>
                                <td>
                                    {isTrashView ? (
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => handleRestoreArtwork(artwork._id)}
                                        >
                                            Renew
                                        </button>
                                    ) : (
                                        <>
                                            <button 
                                                className="btn btn-secondary" 
                                                onClick={() => navigate('/admin/add-artwork/' + artwork._id)}
                                                style={{ marginRight: '8px' }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-danger"
                                                onClick={() => handleDeleteArtwork(artwork._id)}
                                            >
                                                Delete
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                                No artworks found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default AdminArtworks;
